import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useUser } from "../UserContext/UserProvider";
import {
  analyticsSendInterval,
  localStorageSessionKey,
  maxSessionLength,
  sendHeartbeat,
  sendSessionData,
  sessionTimeoutThreshold,
  startNewSession,
  updateSession,
  trackSessionEvent,
} from "./AnalyticsUtils";

// Import Capacitor App for mobile lifecycle events
let App;
try {
  App = require("@capacitor/app").App;
} catch (e) {
  // App module not available (web-only environment)
  App = null;
}

const AnalyticsContext = createContext(null);

export const useAnalytics = () => useContext(AnalyticsContext);

export const AnalyticsProvider = ({ children }) => {
  const { user } = useUser();
  const lastSendTimeRef = useRef(0);

  // Debounced send function to prevent duplicate sends from multiple lifecycle events
  const debouncedSendSessionData = () => {
    const now = Date.now();
    const timeSinceLastSend = now - lastSendTimeRef.current;
    
    // Only send if it's been at least 2 seconds since last send
    // This prevents redundant sends when multiple lifecycle events fire
    if (timeSinceLastSend >= 2000) {
      console.log('🌐 [ANALYTICS] Lifecycle event triggered send');
      lastSendTimeRef.current = now;
      sendSessionData();
    } else {
      console.log(`⏸️ [ANALYTICS] Lifecycle send debounced (${timeSinceLastSend}ms since last)`);
    }
  };

  //& on component mount
  useEffect(() => {
    console.log('🚀 [ANALYTICS] Provider initialized, user:', user?._id || 'anonymous');
    
    const existingSession = JSON.parse(localStorage.getItem(localStorageSessionKey));

    if (existingSession) {
      const sessionAge = Date.now() - existingSession.sessionStart;
      const inactiveTime = Date.now() - existingSession.lastActivity;

      console.log('🔍 [ANALYTICS] Found existing session:', {
        sessionId: existingSession.id,
        age: `${Math.floor(sessionAge / 60000)}min`,
        inactiveFor: `${Math.floor(inactiveTime / 1000)}s`,
        eventsPending: existingSession.events.length,
        userId: existingSession.user || 'anonymous'
      });

      if (
        sessionAge > maxSessionLength ||
        Date.now() - existingSession.lastActivity > sessionTimeoutThreshold ||
        (existingSession.user && user?._id && existingSession.user !== user?._id)
      ) {
        // session is too old, start a new one
        console.log('🔄 [ANALYTICS] Session expired/changed, creating new session');
        sendSessionData().then(() => startNewSession(user));
      } else {
        console.log('✅ [ANALYTICS] Resuming existing session');
        updateSession();
      }
    } else {
      console.log('🆕 [ANALYTICS] No existing session, creating new one');
      startNewSession(user);
    }

    console.log(`⏰ [ANALYTICS] Heartbeat interval set to ${analyticsSendInterval/1000}s`);
    const interval = setInterval(() => {
      sendHeartbeat(user);
    }, analyticsSendInterval);

    return () => {
      console.log('🛑 [ANALYTICS] Provider cleanup');
      clearInterval(interval);
    };
  }, [user]);

  // Lifecycle event handlers to capture analytics before app closes/backgrounds
  useEffect(() => {
    // Handle page visibility changes (tab switching, minimizing, closing)
    // This is the most reliable cross-platform way to catch app state changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Page is hidden (tab switched, minimized, or closing)
        console.log('👋 [ANALYTICS] Page hidden - sending analytics');
        debouncedSendSessionData();
      } else {
        console.log('👁️ [ANALYTICS] Page visible');
      }
    };

    // Handle mobile app going to background (Capacitor)
    let appStateListener;
    if (App) {
      console.log('📱 [ANALYTICS] Capacitor App listener registered');
      appStateListener = App.addListener('pause', () => {
        // App is going to background - send analytics
        console.log('⏸️ [ANALYTICS] App paused - sending analytics');
        debouncedSendSessionData();
      });
    } else {
      console.log('🌐 [ANALYTICS] Running in web mode (no Capacitor)');
    }

    // Add event listeners
    console.log('👂 [ANALYTICS] Lifecycle listeners attached');
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (appStateListener) {
        appStateListener.remove();
      }
    };
  }, []); // Empty deps - these listeners should persist

  const analytics = {
    trackSessionEvent,
  };

  return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>;
};
